import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ChangeRequestsPanel = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchChangeRequests();
  }, [filter]);

  const fetchChangeRequests = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${getApiUrl()}/change-requests`
        : `${getApiUrl()}/change-requests?status=${filter}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': user?.token ? `Bearer ${user.token}` : '',
        },
      });

      const data = await response.json();
      if (data.success) {
        setRequests(data.requests || []);
      } else {
        console.error('Failed to fetch change requests:', data.error);
      }
    } catch (error) {
      console.error('Error fetching change requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setActionLoading(requestId);
      const response = await fetch(`${getApiUrl()}/change-requests/${requestId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user?.token ? `Bearer ${user.token}` : '',
          'X-User': user?.username || '',
        },
      });

      const data = await response.json();
      if (data.success) {
        if (data.auto_rejected) {
          alert('⚠️ ' + (data.message || 'Change request was automatically rejected due to corrupted data. Please delete it and create a new one.'));
        } else {
          alert('Change request approved and applied successfully!');
        }
        fetchChangeRequests(); // Refresh the list
      } else {
        alert('Error approving request: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Enter reason for rejection (optional):');
    if (reason === null) return; // User cancelled

    try {
      setActionLoading(requestId);
      const response = await fetch(`${getApiUrl()}/change-requests/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user?.token ? `Bearer ${user.token}` : '',
          'X-User': user?.username || '',
        },
        body: JSON.stringify({ reason: reason || '' }),
      });

      const data = await response.json();
      if (data.success) {
        alert('Change request rejected successfully!');
        fetchChangeRequests(); // Refresh the list
      } else {
        alert('Error rejecting request: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this change request? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(requestId);
      const response = await fetch(`${getApiUrl()}/change-requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': user?.token ? `Bearer ${user.token}` : '',
          'X-User': user?.username || '',
        },
      });

      const data = await response.json();
      if (data.success) {
        alert('Change request deleted successfully!');
        fetchChangeRequests(); // Refresh the list
      } else {
        alert('Error deleting request: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const parsed = new Date(dateString);
    return Number.isNaN(parsed.getTime()) ? dateString : parsed.toLocaleString();
  };

  const toNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    const str = String(value).replace(/,/g, '').trim();
    if (!str) return null;
    const num = Number(str);
    return Number.isFinite(num) ? num : null;
  };

  const formatValue = (value, field) => {
    const numeric = toNumber(value);
    if (numeric === null) return value === undefined ? 'N/A' : String(value);
    if (field === 'amount') {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numeric);
    }
    if (field === 'er_percentage') {
      return (numeric).toFixed(2) + '%';
    }
    if (['cpv', 'cpe', 'cost_per_comment', 'cost_per_share', 'cost_per_repost'].includes(field)) {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numeric);
    }
    if (field === 'tcs_score') {
      return Math.round(numeric || 0);
    }
    return new Intl.NumberFormat('en-IN').format(Math.round(numeric));
  };

  const getVerdictColor = (verdict) => {
    if (!verdict || typeof verdict !== 'string') {
      return 'bg-gray-100 text-gray-800';
    }
    
    if (verdict.includes('Excellent')) return 'bg-green-100 text-green-800';
    if (verdict.includes('Good')) return 'bg-blue-100 text-blue-800';
    if (verdict.includes('Okay')) return 'bg-yellow-100 text-yellow-800';
    if (verdict.includes('Poor')) return 'bg-red-100 text-red-800';
    if (verdict.includes('No Cost')) return 'bg-purple-100 text-purple-800';
    if (verdict.includes('No Data')) return 'bg-gray-100 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFieldDisplayName = (field) => {
    const fieldNames = {
      'amount': 'Amount',
      'views': 'Views',
      'likes': 'Likes',
      'comments': 'Comments',
      'reposts': 'Reshares',
      'shares': 'Shares'
    };
    return fieldNames[field] || field;
  };

  const resolvePreviousValue = (request) => {
    const prevNum = toNumber(request.previous_value);
    if (prevNum !== null) {
      return { value: prevNum, display: formatValue(prevNum, request.field) };
    }
    // Fallback to current campaign snapshot when previous value is missing/invalid
    const snapshotNum = toNumber(request[request.field]);
    if (snapshotNum !== null) {
      return { value: snapshotNum, display: formatValue(snapshotNum, request.field) };
    }
    return { value: null, display: request.previous_value ?? 'N/A' };
  };

  const resolveRequestedValue = (request) => {
    const nextNum = toNumber(request.requested_value);
    if (nextNum !== null) {
      return { value: nextNum, display: formatValue(nextNum, request.field) };
    }
    return { value: null, display: request.requested_value ?? 'N/A' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600">Loading change requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Change Requests</h2>
          <p className="text-gray-600">Review and approve employee change requests</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={fetchChangeRequests}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-1">
          {[
            { key: 'pending', label: 'Pending', count: requests.filter(r => r.status === 'pending').length },
            { key: 'approved', label: 'Approved', count: requests.filter(r => r.status === 'approved').length },
            { key: 'rejected', label: 'Rejected', count: requests.filter(r => r.status === 'rejected').length },
            { key: 'all', label: 'All', count: requests.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === tab.key
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {requests.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No change requests</h3>
            <p className="text-gray-500">
              {filter === 'pending' 
                ? 'No pending change requests at the moment.'
                : `No ${filter} change requests found.`
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
                    Campaign Details
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Change Request
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Status
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Requested By
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Requested At
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    {/* Campaign Details Column */}
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                      <div className="space-y-2">
                        <div className="font-medium text-blue-600">
                          #{request.campaign_id}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {request.influencer_name}
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div className="flex justify-between">
                            <span>Platform:</span>
                            <span className="font-medium">{request.platform}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Product:</span>
                            <span className="font-medium">{request.product}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Team:</span>
                            <span className="font-medium">{request.team_member || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Tier:</span>
                            <span className="font-medium">{request.tier || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Date:</span>
                            <span className="font-medium">{request.date || 'N/A'}</span>
                          </div>
                        </div>
                        
                        <div className="border-t pt-2">
                          <div className="text-xs font-medium text-gray-700 mb-1">Current Values:</div>
                          <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-2 gap-y-1">
                            <div>Amount: {formatValue(request.amount, 'amount')}</div>
                            <div>Views: {formatValue(request.views, 'views')}</div>
                            <div>Likes: {formatValue(request.likes, 'likes')}</div>
                            <div>Comments: {formatValue(request.comments, 'comments')}</div>
                            <div>Reshares: {formatValue(request.reposts, 'reposts')}</div>
                            <div>Shares: {formatValue(request.shares, 'shares')}</div>
                          </div>
                        </div>

                        <div className="border-t pt-2">
                          <div className="text-xs font-medium text-gray-700 mb-1">Key Metrics:</div>
                          <div className="text-xs space-y-1">
                            <div className="flex justify-between items-center">
                              <span>TCS:</span>
                              <div className="flex items-center space-x-1">
                                <span className="font-medium text-blue-600">{formatValue(request.tcs_score, 'tcs_score')}</span>
                                <span className={`px-1 py-0.5 rounded text-xs ${getVerdictColor(request.tcs_verdict)}`}>
                                  {request.tcs_verdict || 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>ER%:</span>
                              <div className="flex items-center space-x-1">
                                <span className="font-medium text-green-600">{formatValue(request.er_percentage, 'er_percentage')}</span>
                                <span className={`px-1 py-0.5 rounded text-xs ${getVerdictColor(request.er_verdict)}`}>
                                  {request.er_verdict || 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>CPV:</span>
                              <div className="flex items-center space-x-1">
                                <span className="font-medium text-red-600">{formatValue(request.cpv, 'cpv')}</span>
                                <span className={`px-1 py-0.5 rounded text-xs ${getVerdictColor(request.cpv_verdict)}`}>
                                  {request.cpv_verdict || 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>CPE:</span>
                              <div className="flex items-center space-x-1">
                                <span className="font-medium text-purple-600">{formatValue(request.cpe, 'cpe')}</span>
                                <span className={`px-1 py-0.5 rounded text-xs ${getVerdictColor(request.cpe_verdict)}`}>
                                  {request.cpe_verdict || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Change Request Column */}
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                      {(() => {
                        const prevResolved = resolvePreviousValue(request);
                        const nextResolved = resolveRequestedValue(request);
                        const prevNum = prevResolved.value;
                        const nextNum = nextResolved.value;
                        const diffText = (() => {
                          if (prevNum === null || nextNum === null) {
                            return `${nextResolved.display} - ${prevResolved.display}`;
                          }
                          const diff = nextNum - prevNum;
                          if (request.field === 'amount') {
                            return new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            }).format(diff);
                          }
                          return new Intl.NumberFormat('en-IN').format(diff);
                        })();
                        return (
                      <div className="space-y-2">
                        <div className="font-medium text-blue-600 text-center">
                          {getFieldDisplayName(request.field)} Change
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500 text-xs">From:</span>
                            <span className="font-medium text-red-600 text-sm">
                              {prevResolved.display}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500 text-xs">To:</span>
                            <span className="font-medium text-green-600 text-sm">
                              {nextResolved.display}
                            </span>
                          </div>
                          <div className="border-t pt-2">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500 text-xs">Difference:</span>
                              <span className="font-bold text-blue-600 text-sm">
                                {diffText}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                        );
                      })()}
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="font-medium">{request.requested_by}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500 text-center">
                      {formatDate(request.requested_at)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      {request.status === 'pending' ? (
                        <div className="flex space-x-2 justify-center">
                          <button
                            onClick={() => handleApprove(request.id)}
                            disabled={actionLoading === request.id}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === request.id ? (
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            disabled={actionLoading === request.id}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === request.id ? (
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            Reject
                          </button>
                          <button
                            onClick={() => handleDelete(request.id)}
                            disabled={actionLoading === request.id}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete this change request"
                          >
                            {actionLoading === request.id ? (
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-gray-700" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="flex space-x-2 justify-center">
                          <button
                            onClick={() => handleDelete(request.id)}
                            disabled={actionLoading === request.id}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete this change request"
                          >
                            {actionLoading === request.id ? (
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-gray-700" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeRequestsPanel;
